import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { fetchSuppliers, submitBooking } from '../api/registerBookingApi';
import { calculateNights } from '../lib/bookingDates';
import { buildRegisterBookingPayload } from '../lib/bookingPayloadMapper';
import { getEligibleRoomTypes } from '../lib/roomTypeRules';
import { getLanguageSection } from '../../../shared/services/languageService';

const fieldClass = "space-y-2";
const labelClass = "m-0 block text-xs font-medium text-[#151b3f]";
const inputClass = "h-11 w-full rounded-lg border border-slate-200 bg-white px-4 text-sm font-normal text-[#151b3f] shadow-none outline-none transition placeholder:text-slate-400 focus:border-[#4aa6cf] focus:ring-4 focus:ring-[#4aa6cf]/10 disabled:bg-slate-100 disabled:text-slate-500";
const selectClass = inputClass;

function createInitialBooking() {
  return {
    BookingNumber: '',
    ClientName: '',
    ArrivalDate: new Date(),
    DepartureDate: new Date(),
    roomType: '',
    packageCost: '',
    totalRoom: '',
    menuTour: 'none',
    enteredBy: '',
    country: Cookies.get('CountryUser'),
    isgroup: 0,
    groupnumber: 0
  };
}

function BookingFormRow({ index, formItemNumber, roomTypes }) {
  const [booking, setBooking] = useState(createInitialBooking);
  const [disabled, setDisabled] = useState(false);
  const [nights, setNights] = useState('0');
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showGroupNumber, setShowGroupNumber] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const language = getLanguageSection('registerbooking');
  const countryUser = Cookies.get('CountryUser');

  useEffect(() => {
    fetchSuppliers()
      .then((data) => setSuppliers(data || []))
      .catch(() => setSuppliers([]));
  }, []);

  const handleInputChange = (event) => {
    setErrorMessage('');

    const { name, value } = event.target;
    const [, field] = name.split('.');

    setBooking((currentBooking) => {
      const nextBooking = {
        ...currentBooking,
        [field]: value
      };

      if (name === 'booking.isgroup') {
        if (String(value) === '-1') {
          nextBooking.roomType = 13;
        }
      }

      return nextBooking;
    });

    if (name === 'booking.isgroup') {
      setShowGroupNumber(String(value) === '-1');
    }
  };

  const handleSubmit = async () => {
    setErrorMessage('');
    setLoading(true);

    const payload = buildRegisterBookingPayload(booking, {
      userAccessToken: Cookies.get('UserAccessToken'),
      deviceAccessToken: Cookies.get('DeviceAccessToken')
    });

    try {
      await submitBooking(payload);
      setDisabled(true);
      setLoading(false);
    } catch (error) {
      setErrorMessage(error?.payload?.errorMessage || error.message);
      setLoading(false);
    }
  };

  const handleArrivalDateChange = (date) => {
    setBooking((currentBooking) => {
      const nextBooking = { ...currentBooking, ArrivalDate: date };
      const nightsData = calculateNights(nextBooking.ArrivalDate, nextBooking.DepartureDate);

      nextBooking.totalRoom = nightsData.totalRoom;
      setNights(nightsData.nights);

      return nextBooking;
    });
  };

  const handleDepartureDateChange = (date) => {
    setBooking((currentBooking) => {
      const nextBooking = { ...currentBooking, DepartureDate: date };
      const nightsData = calculateNights(nextBooking.ArrivalDate, nextBooking.DepartureDate);

      nextBooking.totalRoom = nightsData.totalRoom;
      setNights(nightsData.nights);

      return nextBooking;
    });
  };

  function roomTypesOptions() {
    const eligibleRoomTypes = getEligibleRoomTypes(roomTypes, booking.ArrivalDate, Number(countryUser));

    return [
      <option key={-1} value="-1">{language.select}</option>,
      ...eligibleRoomTypes.map((roomType, index_) => (
        <option key={index_} value={roomType.Id_MarivalRoomType}>{roomType.Name}</option>
      ))
    ];
  }

  function suppliersOptions(idcountry) {
    const options = [<option key="-1" value="-1">{language.select}</option>];

    suppliers.forEach((supplier, index_) => {
      if (idcountry === Number(supplier.Id_Country) || idcountry === Number(supplier.Id_Country2) || idcountry === Number(supplier.Id_Country3)) {
        options.push(<option key={`${supplier.Name}-${index_}`} value={supplier.Name}>{supplier.Name}</option>);
      }
    });

    return options;
  }

  const minDate = countryUser === '167' ? new Date('2024-01-02T00:00:00') : new Date('2023-01-02T00:00:00');

  const renderBookingSource = () => {
    if (countryUser === '167') {
      return (
        <select disabled={disabled} className={selectClass} value={booking.Id_BookingSource} onChange={handleInputChange} name='booking.Id_BookingSource'>
          {suppliersOptions(167)}
        </select>
      );
    }

    if (countryUser === '254') {
      return (
        <select disabled={disabled} className={selectClass} value={booking.Id_BookingSource} onChange={handleInputChange} name='booking.Id_BookingSource'>
          {suppliersOptions(254)}
        </select>
      );
    }

    if (countryUser === '40') {
      return (
        <select disabled={disabled} className={selectClass} value={booking.Id_BookingSource} onChange={handleInputChange} name='booking.Id_BookingSource'>
          {suppliersOptions(40)}
        </select>
      );
    }

    return (
      <select disabled={disabled} className={selectClass} value={booking.Id_BookingSource} onChange={handleInputChange} name='booking.Id_BookingSource'>
        <option value="none">{language.select}</option>
      </select>
    );
  };

  return (
    <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className={"row_" + index}>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <h3 className="m-0 text-2xl font-semibold text-[#2b86d1]">{language.bookingLabel}{index}</h3>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#eaf6fb] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[#2b86d1]">
            {language.sendbtn}
          </span>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-12">
          <div className={`${fieldClass} xl:col-span-3`}>
            <label className={labelClass}>{language.bookingSource}</label>
            {renderBookingSource()}
          </div>

          <div className={`${fieldClass} xl:col-span-2`}>
            <label className={labelClass}>{language.bookingNumber}</label>
            <input disabled={disabled} type="text" className={inputClass} placeholder={language.holderNumber} onChange={handleInputChange} name='booking.BookingNumber' value={booking.BookingNumber} />
          </div>

          <div className={`${fieldClass} xl:col-span-2`}>
            <label className={labelClass}>{language.clientName}</label>
            <input disabled={disabled} type="text" className={inputClass} placeholder={language.holderName} onChange={handleInputChange} name='booking.ClientName' value={booking.ClientName} />
          </div>

          <div className={`${fieldClass} xl:col-span-2`}>
            <label className={labelClass}>{language.resort}</label>
            <select disabled={disabled} className={selectClass} value={booking.Id_MarivalHotel} onChange={handleInputChange} name='booking.Id_MarivalHotel'>
              <option value="-1">{language.select}</option>
              <option value="1">Marival&reg; Emotions</option>
              <option value="2">Marival&reg; Distinct</option>
              <option value="3">Marival&reg; Armony</option>
            </select>
          </div>

          <div className={`${fieldClass} xl:col-span-3`}>
            <label className={labelClass}>{language.isThisAGroup}</label>
            <select disabled={disabled} className={selectClass} value={booking.isgroup} onChange={handleInputChange} name='booking.isgroup'>
              <option value="0">{language.groupno}</option>
              <option value="-1">{language.groupyes}</option>
            </select>
          </div>

          <div id={"numberGroup" + formItemNumber} style={{ display: showGroupNumber ? undefined : 'none' }} className={`${fieldClass} xl:col-span-2`}>
            <label className={labelClass}>{language.groupNumber}</label>
            <input disabled={disabled} type="text" className={inputClass} placeholder={language.holderNumber1} onChange={handleInputChange} name='booking.groupnumber' value={booking.groupnumber} />
          </div>

          <div className={`${fieldClass} xl:col-span-2`}>
            <label className={labelClass}>{language.date}</label>
            {disabled === false ?
              <DatePicker wrapperClassName="w-full" minDate={minDate} disabled={disabled} className={inputClass} selected={booking.ArrivalDate} onChange={handleArrivalDateChange} />
              :
              <input disabled={true} type="text" className={inputClass} value={booking.ArrivalDate} />}
          </div>

          <div className={`${fieldClass} xl:col-span-2`}>
            <label className={labelClass}>{language.date1}</label>
            {disabled === false ?
              <DatePicker wrapperClassName="w-full" minDate={minDate} disabled={disabled} className={inputClass} selected={booking.DepartureDate} onChange={handleDepartureDateChange} />
              :
              <input disabled={true} type="text" className={inputClass} value={booking.DepartureDate} />}
          </div>

          <div className={`${fieldClass} xl:col-span-2`}>
            <label className={labelClass}>{language.room}</label>
            <select disabled={disabled} className={selectClass} value={booking.roomType} onChange={handleInputChange} name='booking.roomType'>
              {roomTypesOptions()}
            </select>
          </div>

          <div className={`${fieldClass} xl:col-span-2`}>
            <label className={labelClass}>{language.roomTotal}</label>
            <input disabled={true} type="text" className={inputClass} placeholder={nights} onChange={handleInputChange} name='booking.totalRoom' value={booking.totalRoom} />
          </div>

          <div className={`${fieldClass} xl:col-span-2`}>
            <label className={labelClass}>{language.packagetotalcost}</label>
            <input type="text" className={inputClass} placeholder='0.00' onChange={handleInputChange} name='booking.packageCost' value={booking.packageCost} />
          </div>

          {countryUser === '167' ?
            <div className={`${fieldClass} xl:col-span-2`}>
              <label className={labelClass}>{language.enteredBy}</label>
              <select disabled={disabled} className={selectClass} value={booking.enteredBy} onChange={handleInputChange} name='booking.enteredBy'>
                <option value="-1">{language.select}</option>
                <option value="Alejandra">Alejandra</option>
                <option value="Abril">Abril</option>
                <option value="Brenda ">Brenda</option>
                <option value="Mirian">Mirian</option>
                {/* El value se manda al backend tal cual: solo se traduce la etiqueta visible. */}
                <option value="Varios">{language.various}</option>
                <option value="Otro">{language.other}</option>
              </select>
            </div> : null}

          {countryUser === '254' ?
            <div className={`${fieldClass} xl:col-span-2`}>
              <label className={labelClass}>{language.enteredBy}</label>
              <select disabled={disabled} className={selectClass} value={booking.enteredBy} onChange={handleInputChange} name='booking.enteredBy'>
                <option value="-1">{language.select}</option>
                <option value="Nitzy">Nitzy</option>
                <option value="Marc">Marc</option>
                <option value="Various">{language.various}</option>
                <option value="Other">{language.other}</option>
              </select>
            </div> : null}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{language.bookingLabel}{index}</span>

          {disabled ?
            null
            :
            <button disabled={disabled} onClick={handleSubmit} className="min-h-[44px] rounded-xl bg-marival-sky px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-[#276cae]"><span>{language.sendbtn}</span></button>}
        </div>

        {loading ? <div className="mt-4 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-600">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#4aa6cf] border-t-transparent"></div>
          <p className="m-0">{language.sendingMessage} <span className="dots"><span>.</span><span>.</span><span>.</span></span></p>
        </div>
          : ''}

        {disabled ? (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">
            <span>{language.successRowMessage}</span>
          </div>
        ) : null}

        {errorMessage ? (
          <div className="ErrorMessage mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold leading-6 text-red-600">{errorMessage}</div>
        ) : null}
      </div>
    </div>
  );
}

export default BookingFormRow;
